import axios from 'axios';
import { API_BASE_URL, API_TIMEOUT, API_HEADERS, API_ENDPOINTS } from '../config/api';
import authService from './authService';
import { publishPoints } from './pointsChannel.js';
import { publishCredits } from './creditsChannel.js';
import { sessionExpired } from '../redux/actions/authActions';
import { isSafari } from '../utils/browserUtils';

// Safari caps XHR connections at ~60-90s in practice regardless of the JS timeout.
// Clamp long-running requests to 180s per attempt on Safari so error handling is
// predictable (safariPost in chatApiService will retry up to 3 times).
const SAFARI_MAX_TIMEOUT = 180000;

let store;

export const injectStore = (_store) => {
  store = _store;
};

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 900000,
  headers: API_HEADERS
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = authService.getTokenFromCookie();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Clamp timeout on Safari to avoid XHR long-request kills (ERR_NETWORK)
    if (isSafari() && config.timeout > SAFARI_MAX_TIMEOUT) {
      config.timeout = SAFARI_MAX_TIMEOUT;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => {
    try {
      const body = response?.data;
      const isPlainObject = body && typeof body === 'object' && !Array.isArray(body);

      // Helper to extract and validate number
      const extractValue = (prop) => {
        let val = isPlainObject ? body?.[prop] : undefined;
        
        // Check nested in data object
        if (val === undefined && isPlainObject && body?.data && typeof body.data === 'object' && !Array.isArray(body.data)) {
          val = body.data[prop];
        }

        // Check headers
        if (val === undefined && response?.headers) {
          val = response.headers[`x-user-${prop}`] ?? response.headers[`X-User-${prop.charAt(0).toUpperCase() + prop.slice(1)}`];
        }

        const num = Number(val);
        return Number.isFinite(num) ? num : undefined;
      };

      const points = extractValue('points');
      if (points !== undefined) {
        publishPoints(points);
      }

      const credits = extractValue('credits');
      if (credits !== undefined) {
        publishCredits(credits);
      }
    } catch { }
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;
    const refreshToken = authService.getRefreshToken();

    // Check if user is viewing a shared chat - don't logout in this case
    const isViewingSharedChat = () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.has('shareId');
      } catch {
        return false;
      }
    };

    const isSessionExpired = error.response?.data?.message === 'SESSION_EXPIRED';

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      if (isSessionExpired && refreshToken) {
        if (isRefreshing) {
          return new Promise(function (resolve, reject) {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers['Authorization'] = 'Bearer ' + token;
              return api(originalRequest);
            })
            .catch(err => Promise.reject(err));
        }
        originalRequest._retry = true;
        isRefreshing = true;
        try {
          const newAccessToken = await authService.refreshToken();
          processQueue(null, newAccessToken);
          originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          authService.clearTokens();
          // Don't trigger session expired if viewing shared chat
          if (!isViewingSharedChat()) {
            if (store) {
              store.dispatch(sessionExpired());
            } else {
              window.location.href = '/login';
            }
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else if (isSessionExpired && !refreshToken) {
        // If SESSION_EXPIRED but no refresh token, trigger session expired
        authService.clearTokens();
        if (!isViewingSharedChat()) {
          if (store) {
            store.dispatch(sessionExpired());
          } else {
            window.location.href = '/login';
          }
        }
      }
      // For other 401 errors (like "Invalid email", "Invalid credentials"), 
      // just reject the error without triggering session expired
    }

    return Promise.reject(error);
  }
);

api.fetchFullSupremeCourtCaseDetails = (diary_no, diary_year) => {
  return api.post('/api/v1/supremecourtinfo/fetch-full-details', { diary_no, diary_year });
};

api.editDraftByAi = (payload) => {
  return api.post('/api/v1/custom-draft/llm/edit_draft_by_ai', payload);
};

api.getFeatureSummary = () => api.get('/api/v1/wallet/feature-summary/');
api.getTransactions = (page = 1, limit = 10, type = 'all') => {
  let url = `/api/v1/wallet/transactions/?page=${page}&limit=${limit}`;
  if (type && type !== 'all') {
    url += `&type=${type}`;
  }
  return api.get(url);
};
api.redeemCoupon = (couponCode) => api.post('/api/v1/wallet/redeem-coupon', { couponCode });
api.convertPoints = (pointsToConvert) => api.post('/api/v1/wallet/convert-points', { pointsToConvert });
api.getUserBalance = () => api.get('/api/v1/wallet/balance');
api.getSubscribedCases = () => api.get('/api/v1/subscribed-cases');
api.getSubscribedCaseDetails = (caseId) => api.get(`/api/v1/subscribed-cases/${caseId}`);
api.getCalendarEvents = () => api.get('/api/v1/calendar-events');

api.extractEventsByDocumentId = (documentId) => {
  return api.post('/api/v1/event-extraction/extract-by-document-id', {
    documentId: documentId
  }, {
    timeout: 900000, // 15 minutes timeout for large documents
  });
};

api.linkdinPostGenerate = ({ topic, tone, wordLimit: selectedWordLimit, language }) => {
  return api.post('/api/v1/linkedin-post/generate', {
    topic, tone, wordLimit: selectedWordLimit, language
  });
};

api.uploadDocument = (formData, onUploadProgress = null) => {
  return api.post('/api/v1/documents/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 900000, // 15 minutes timeout for large documents
    onUploadProgress: onUploadProgress, // Support progress tracking
  });
};

api.downloadEventsPDF = (eventsData) => {
  return api.post('/api/v1/event-extraction/download-pdf', eventsData, {
    responseType: 'blob',
    timeout: 900000, // 15 minutes timeout for PDF generation
  });
};

// Master List Generation APIs
api.generateMasterListPdf = (masterListData) => {
  return api.post('/api/v1/event-extraction/generate-master-list-pdf', masterListData, {
    responseType: 'blob',
    timeout: 900000, // 15 minutes timeout for PDF generation
  });
};

api.generateMasterListDocx = (masterListData) => {
  return api.post('/api/v1/event-extraction/generate-master-list-docx', masterListData, {
    responseType: 'blob',
    timeout: 900000, // 15 minutes timeout for DOCX generation
  });
};

api.feedback = (payload) => {
  return api.post('/api/v1/feedback/submit', payload);
}

// --- Notification APIs ---
api.getNotifications = ({ page = 1, pageSize = 10, read, type } = {}) => {
  const params = new URLSearchParams();
  if (page) params.append('page', page);
  if (pageSize) params.append('pageSize', pageSize);
  if (read !== undefined) params.append('read', read);
  if (type) params.append('type', type);
  return api.get(`/api/v1/notifications?${params.toString()}`);
};

api.markNotificationRead = (id) => api.patch(`/api/v1/notifications/${id}/read`);
api.markAllNotificationsRead = () => api.patch('/api/v1/notifications/read-all');

api.getTranslationHistory = () => {
  return api.get('/api/v1/translator/history');
};

api.getTranslationHistoryDetails = (id) => {
  return api.get(`/api/v1/translator/history/${id}`);
};

api.fetchExternalBlob = (url) => {
  // We use a direct axios.get call here because the URL is external to our API's baseURL.
  return axios.get(url, {
    responseType: 'blob', // Crucial for handling file data
    timeout: 60000, // 60-second timeout for the file download
  }).then(response => response.data); // The response interceptor is bypassed, so we take .data here
};

// --- Logout API with refresh token in Authorization header ---
api.logout = () => {
  const refreshToken = authService.getRefreshToken();
  if (!refreshToken) {
    return Promise.reject(new Error('No refresh token available'));
  }

  return axios.post(`${API_BASE_URL}${API_ENDPOINTS.AUTH.LOGOUT}`, {}, {
    headers: {
      ...API_HEADERS,
      'Authorization': `Bearer ${refreshToken}`
    },
    timeout: API_TIMEOUT
  });
};

// Event Extraction History APIs
api.getEventExtractionHistory = (page = 1, limit = 10) => {
  return api.get('/api/v1/event-extraction/history', {
    params: { page, limit },
  });
};

api.searchEventExtractionDocuments = (query, page = 1, limit = 20) => {
  return api.get('/api/v1/event-extraction/search/by-name', {
    params: { q: query, page, limit },
  });
};

api.renameEventExtractionDocument = (documentId, filename) => {
  return api.put(`/api/v1/event-extraction/documents/${documentId}/rename`, {
    filename,
  });
};

api.deleteEventExtractionDocument = (documentId) => {
  return api.delete(`/api/v1/event-extraction/documents/${documentId}`);
};

// --- Legal Notice APIs ---
api.generateLegalNotice = (documentId, userPrompt, type = 'legalNotice', forceRegenerate = false, version = 'v2') => {
  return api.post('/api/v1/legal-notice/generate', { documentId, userPrompt, type, forceRegenerate, version }, {
    timeout: 600000, // 10 min — multi-agent V2 can be slower
  });
};

api.getLegalNoticeResult = (documentId, type = 'legalNotice') => {
  return api.get(`/api/v1/legal-notice/${documentId}`, { params: { type } });
};

api.getLegalNoticeHistory = (page = 1, limit = 10) => {
  return api.get('/api/v1/legal-notice/history', { params: { page, limit } });
};

api.deleteLegalNotice = (id) => {
  return api.delete(`/api/v1/legal-notice/${id}`);
};

api.downloadLegalNotice = (notice, format = 'pdf') => {
  const mime = format === 'docx'
    ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    : 'application/pdf';
  return api.post(
    `/api/v1/legal-notice/download/${format}`,
    { notice },
    {
      responseType: 'blob',
      timeout: 60000,
      headers: { Accept: mime },
    }
  );
};

export default api;
